import { Slot } from "@fluentui/react-components";

import React from "react";

export enum CardSize {
    ExtraSmall = 100 / 4,
    Small = 100 / 3,
    Medium = 50,
    Large = 200 / 3,
    ExtraLarge = 100
}

export type CardSpace = "LSpace" | "XLSpace" | "XXLSpace";

/**
 * Data sent from the selection events on a selectable card.
 */
export declare type CardOnSelectData = {
    selected: boolean;
};

/**
 * Card selected event type
 *
 * This event is fired when a selectable card changes its selection state.
 */
export declare type CardOnSelectionChangeEvent = React.MouseEvent | React.KeyboardEvent | React.ChangeEvent;

export interface IM365PulseCardConfigMenuItem {
    key: string;
    /**
     * Displayed title of the item.
     */
    title: string;
    /**
     * Icon of the item. It is displayed before above title.
     */
    icon?: Slot<"span">;
    /**
     * Callback function of the item. Will be executed when user clicks the item.
     */
    onClick?: () => void;
}

export type M365PulseCardProps = {
    /**
     * Predefined width.
     * @default CardSize.ExtraLarge
     */
    width?: CardSize;

    /**
     * If provided, it will be displayed as the header with predefined styles.
     */
    title?: string | React.ReactElement;

    /**
     * If provided, it will be displayed as a secondary header with predefined styles.
     */
    summary?: string | React.ReactElement;

    /**
     * If provided, it will be displayed as config summary.
     */
    configSummary?: Record<string, string | string[] | React.ReactElement>;

    /**
     * If provided, if we show the config summary by default.
     */
    isConfigSummaryOpen?: boolean;

    /**
     * Configuration items. If provided, will show config icon in the header. Click the icon will open MenuList of this items.
     */
    configMenuItems?: IM365PulseCardConfigMenuItem[];

    /**
     * The buttons showing in the footer
     */
    footerActions?: React.ReactNode;

    /**
     * Aria-label for the footer actions.
     * If not provided it will take the value of `title` property.
     * @remarks
     * If title is not of type `string` then the value of `footerActionsAriaLabel` will be undefined.
     */
    footerActionsAriaLabel?: string;

    /**
     * The content in the card.
     */
    children?: React.ReactNode;

    /**
     * If provided, it will enable the copy image functionality
     */
    copyImageEnabled?: boolean;

    /**
     * Sets class names for the child components.
     */
    classNames?: M365PulseCardStyle;

    /**
     * If true, the card will be highlighted with special style of border and shadow.
     */
    highlighted?: boolean;

    /**
     * If provided, it shows an static duration text to the left of the config menu
     */
    durationText?: string;

    /**
     * If provided, it shows an additional action to the right of the config menu
     */
    additionalAction?: React.ReactElement;

    /**
     * If provided, action will be done when mouse enters the card
     */
    onMouseEnter?: () => void;

    /**
     * If provided, action will be done when mouse leaves the card
     */
    onMouseLeave?: () => void;

    /**
     * The callback function when the card is clicked.
     */
    onClick?: () => void;

    /**
     * Define extra space between cards.
     */
    space?: CardSpace;

    /**
     * Indicates if pin icon should be shown to allow pinning of associated view to home dashboard.
     */
    dashboardPin?: boolean;

    /**
     * Unique ID of card/widget that should be pinned to dashboard.
     */
    dashboardCardRegistryId?: string;

    /**
     * A property bag to store the config of the card.
     */
    dashboardCardConfig?: object;

    /**
     * If true, the card will show scroll bar when the content is overflow.
     */
    showScrollBar?: boolean;

    /**
     * If provided, we show the refresh button.
     */
    refresh?: () => void;

    /**
     * Defines the controlled selected state of the card.
     *
     * @default false
     */
    selected?: boolean;
    /**
     * Defines whether the card is initially in a selected state when rendered.
     *
     * @default false
     */
    defaultSelected?: boolean;

    /**
     * Callback to be called when the selected state value changes.
     */
    onSelectionChange?: (event: CardOnSelectionChangeEvent, data: CardOnSelectData) => void;

    /**
     * Defines whether the header actions is always shown. If false, header actions only show when hovered.
     */
    alwaysShowHeaderActions?: boolean;

    /**
     * If provided, the card can change the layout
     */
    onLayoutChange?: (id: string, direction: "Right" | "Left" | "Up" | "Down", changeType: "Move" | "Resize") => void;

    /**
     * Sets the focus behavior for the card.
     *
     * @default "no-tab"
     */
    focusMode?: "off" | "no-tab" | "tab-exit" | "tab-only";
};

export type M365PulseCardStyle = {
    /**
     * Card root class name.
     */
    root?: string;

    /**
     * Header class name.
     */
    header?: string;

    /**
     * Body class name.
     */
    body?: string;

    /**
     * footer class name.
     */
    footer?: string;

    /**
     * summary class name.
     */
    summary?: string;
};

export interface IM365PulseCardStyleState {
    width: CardSize;
    space?: CardSpace;
}
